import { never } from "@vendrinc/frontend-ts";
import * as React from "react";
import { AuthenticationContext } from "~/contexts/authenticationContext";

export default usePlatformAuthentication;

/**
 * This hook will listen for the Platform app loaded into the iFrame reference that's returned, and notify
 * callers when/if the user is authenticated.
 *
 * @param input An optional set of callabacks that can be used to listen for various auth events
 * @returns A bool to indicate if the user is authenticated with the Platform app, and a reference
 * to the iframe that the app is loaded into. Callers must pass it to the `ref` attribute of the
 * iframe.
 */
export function usePlatformAuthentication(input: CallbacksInput = {}): {
  isAuthenticatedWithVendrPlatform: boolean;
  authenticationInProgress: boolean;
  frameRef: React.MutableRefObject<HTMLIFrameElement | null>;
  disconnect: () => void;
} {
  const data = React.useContext(AuthenticationContext);

  const { isAuthenticatedWithVendrPlatform } = data
    ? {
        isAuthenticatedWithVendrPlatform: data.isAuthenticatedWithVendrPlatform,
      }
    : { isAuthenticatedWithVendrPlatform: false };

  const [state, setState] = React.useState<State>({
    isAuthenticatedWithVendrPlatform: isAuthenticatedWithVendrPlatform,
    authenticationInProgress: false,
    frameRef: React.useRef<HTMLIFrameElement | null>(null),
    connection: React.useRef<PlatformConnection | null>(null),
  });

  const { frameRef, connection } = state;

  const callbacks = useCallbacks(input);

  React.useEffect(() => {
    if (
      typeof data?.isAuthenticatedWithVendrPlatform === "boolean" &&
      data.isAuthenticatedWithVendrPlatform !==
        state.isAuthenticatedWithVendrPlatform
    ) {
      setState((currentState) => ({
        ...currentState,
        isAuthenticatedWithVendrPlatform: data.isAuthenticatedWithVendrPlatform,
      }));
    }
  }, [
    data?.isAuthenticatedWithVendrPlatform,
    state.isAuthenticatedWithVendrPlatform,
  ]);

  React.useEffect(() => {
    if (!connection.current) {
      connection.current = new PlatformConnection(
        setState,
        callbacks,
        frameRef,
      );
    }

    const platform = connection.current;

    platform.subscribe();

    return () => {
      platform.unsubscribe();
    };
  }, [callbacks, frameRef, connection]);

  return {
    isAuthenticatedWithVendrPlatform: state.isAuthenticatedWithVendrPlatform,
    authenticationInProgress: state.authenticationInProgress,
    frameRef,
    disconnect: () => {
      if (connection.current) {
        connection.current.unsubscribe();
      }
    },
  };
}

export type CallbackProps<T = Record<string, never>> = T & {
  frame: React.RefObject<HTMLIFrameElement>;
};

export type CallbacksInput = {
  onCodeAuthInitiated?: (props: CallbackProps) => void;
  onCodeAuthInProgress?: (props: CallbackProps) => void;
  onCodeAuthFailed?: (props: CallbackProps) => void;
  onGoogleAuthInitiated?: (props: CallbackProps) => void;
  onGoogleAuthInProgress?: (props: CallbackProps) => void;
  onGoogleAuthFailed?: (props: CallbackProps) => void;
  onSamlAuthInitiated?: (props: CallbackProps) => void;
  onSamlAuthInProgress?: (props: CallbackProps) => void;
  onSamlAuthFailed?: (props: CallbackProps) => void;
  onAuthSucceeded?: (props: CallbackProps) => void;
  onRedirectIframe?: (props: CallbackProps<{ redirectTo: string }>) => void;
  onOAuthCancelled?: (props: CallbackProps) => void;
};

type Callbacks = Required<CallbacksInput>;

const PlatformConnectEventType = {
  Connect: "Connect",
} as const;

const PlatformAuthEventType = {
  CodeAuthInitiated: "CodeAuthInitiated",
  CodeAuthInProgress: "CodeAuthInProgress",
  CodeAuthFailed: "CodeAuthFailed",

  GoogleAuthInitiated: "GoogleAuthInitiated",
  GoogleAuthInProgress: "GoogleAuthInProgress",
  GoogleAuthFailed: "GoogleAuthFailed",

  SamlAuthInitiated: "SamlAuthInitiated",
  SamlAuthInProgress: "SamlAuthInProgress",
  SamlAuthFailed: "SamlAuthFailed",

  AuthSucceeded: "AuthSucceeded",
  RedirectIframe: "RedirectIframe",
  OAuthCancelled: "OAuthCancelled",
} as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MarketplaceAuthEventType = {
  AuthFailed: "AuthFailed",
} as const;

export type MarketplaceAuthEvent = {
  tag: typeof MarketplaceAuthEventType.AuthFailed;
  reason: string;
};

const PlatformAuthFailedReason = {
  AuthError: "AuthError",
  Cancelled: "Cancelled",
  Timeout: "Timeout",
  PopupsBlocked: "PopupsBlocked",
} as const;

type PlatformAuthFailedReason =
  (typeof PlatformAuthFailedReason)[keyof typeof PlatformAuthFailedReason];

type PlatformConnectEvent = {
  tag: typeof PlatformConnectEventType.Connect;
  nonce: string;
};

type PlatformAuthEvent =
  /* Code Authentication */
  | { tag: typeof PlatformAuthEventType.CodeAuthInitiated; email: string }
  | { tag: typeof PlatformAuthEventType.CodeAuthInProgress; code: string }
  | {
      tag: typeof PlatformAuthEventType.CodeAuthFailed;
      reason: PlatformAuthFailedReason;
    }
  /* Google Authentication */
  | { tag: typeof PlatformAuthEventType.GoogleAuthInitiated }
  | { tag: typeof PlatformAuthEventType.GoogleAuthInProgress; code: string }
  | {
      tag: typeof PlatformAuthEventType.GoogleAuthFailed;
      reason: PlatformAuthFailedReason;
    }
  /* Saml Authentication */
  | { tag: typeof PlatformAuthEventType.SamlAuthInitiated }
  | { tag: typeof PlatformAuthEventType.SamlAuthInProgress }
  | {
      tag: typeof PlatformAuthEventType.SamlAuthFailed;
      reason: PlatformAuthFailedReason;
    }
  | {
      tag: typeof PlatformAuthEventType.AuthSucceeded;
    }
  | { tag: typeof PlatformAuthEventType.RedirectIframe; redirectTo: string }
  | { tag: typeof PlatformAuthEventType.OAuthCancelled };

type IncomingWindowMessage = MessageEvent<PlatformConnectEvent>;
type IncomingPlatformMessage = MessageEvent<PlatformAuthEvent>;

export type State = {
  isAuthenticatedWithVendrPlatform: boolean;
  authenticationInProgress: boolean;
  frameRef: React.MutableRefObject<HTMLIFrameElement | null>;
  connection: React.MutableRefObject<PlatformConnection | null>;
};

function useCallbacks(input: CallbacksInput): Callbacks {
  const defaultCallback = React.useCallback<() => void>(() => undefined, []);

  const onCodeAuthInitiated = input.onCodeAuthInitiated ?? defaultCallback;
  const onCodeAuthInProgress = input.onCodeAuthInProgress ?? defaultCallback;
  const onCodeAuthFailed = input.onCodeAuthFailed ?? defaultCallback;
  const onGoogleAuthInitiated = input.onGoogleAuthInitiated ?? defaultCallback;
  const onGoogleAuthInProgress =
    input.onGoogleAuthInProgress ?? defaultCallback;
  const onGoogleAuthFailed = input.onGoogleAuthFailed ?? defaultCallback;
  const onSamlAuthInitiated = input.onSamlAuthInitiated ?? defaultCallback;
  const onSamlAuthInProgress = input.onSamlAuthInProgress ?? defaultCallback;
  const onSamlAuthFailed = input.onSamlAuthFailed ?? defaultCallback;
  const onAuthSucceeded = input.onAuthSucceeded ?? defaultCallback;
  const onRedirectIframe = input.onRedirectIframe ?? defaultCallback;
  const onOAuthCancelled = input.onOAuthCancelled ?? defaultCallback;

  const callbacks = React.useMemo(() => {
    return {
      onCodeAuthInitiated,
      onCodeAuthInProgress,
      onCodeAuthFailed,
      onGoogleAuthInitiated,
      onGoogleAuthInProgress,
      onGoogleAuthFailed,
      onSamlAuthInitiated,
      onSamlAuthInProgress,
      onSamlAuthFailed,
      onAuthSucceeded,
      onRedirectIframe,
      onOAuthCancelled,
    };
  }, [
    onCodeAuthInitiated,
    onCodeAuthInProgress,
    onCodeAuthFailed,
    onGoogleAuthInitiated,
    onGoogleAuthInProgress,
    onGoogleAuthFailed,
    onSamlAuthInitiated,
    onSamlAuthInProgress,
    onSamlAuthFailed,
    onAuthSucceeded,
    onRedirectIframe,
    onOAuthCancelled,
  ]);

  return callbacks;
}

/**
 * Manages the event listeners that listen for messages from the Platform app's
 * browser Window, and the dedicated communication port that's set up between
 * the two.
 *
 * We need to be very careful to ensure that at any given point in time, there's
 * exactly *one* event listener attached to the window and/or the port. If we don't,
 * we'll receive multiples of the same message sent by the Platform app, which will
 * mess up how we handle the state of the auth flow on the Marketplace side.
 *
 * This is hard to manage given that hooks / custom hooks are called on
 * each render, and we can't rely on a render *not* happening for the hook to
 * function properly. A hook must be resilient to re-renders.
 *
 * The general recommendation for dealing with with re-renders is to make sure that
 * a custom hook which creates resources outside of the render loop cleans those
 * resources up on each re-render.
 *
 * The state that's stored that is scoped to the component in which this hook is
 * used is easier to manage, because if the component is gone (unmounted), then
 * the references are gone. But there's also a listener attached to the Window
 * object, which is global and lives as long as the page does, so we need to do
 * manual cleanup. If we don't, each time the auth dialog is opened + closed, we
 * attach an additional even listener.
 *
 * This class abstracts the management of all of that, exposing just two public methods,
 * `subscribe` and `unsubscribe`.
 *
 * When the component renders, the hook is called and the connection is created +
 * `subscribe` is called. When React re-renders, `unsubscribe` is called and then
 * we re-subscribe.
 */
class PlatformConnection {
  private readonly callbacks: Callbacks;
  private readonly setState: React.Dispatch<React.SetStateAction<State>>;
  private readonly frame: React.MutableRefObject<HTMLIFrameElement | null>;
  private readonly window: PlatformChannel<
    Window,
    PlatformConnectEvent,
    PlatformConnectEvent
  >;
  private readonly port: PlatformChannel<
    MessagePort,
    PlatformAuthEvent,
    MarketplaceAuthEvent
  > | null;

  constructor(
    setState: React.Dispatch<React.SetStateAction<State>>,
    callbacks: Callbacks,
    frame: React.MutableRefObject<HTMLIFrameElement | null>,
  ) {
    this.setState = setState;
    this.callbacks = callbacks;
    this.frame = frame;
    this.window = new PlatformChannel(
      window,
      (message): message is IncomingWindowMessage => {
        if (message.data && typeof message.data === "object") {
          return "tag" in message.data;
        }
        return false;
      },
    );
    this.port = null;
  }

  subscribe() {
    this.window.subscribe((message) => {
      switch (message.data.tag) {
        case PlatformConnectEventType.Connect: {
          this.platformMessageListener(message);
          break;
        }
        default:
          return never(message.data.tag);
      }
    });
  }

  unsubscribe() {
    if (this.port) {
      this.port.unsubscribe();
    }

    this.window.unsubscribe();
  }

  private platformMessageListener(connectMessage: IncomingWindowMessage) {
    if (this.port) {
      return;
    }

    const port = new PlatformChannel(
      connectMessage.ports[0],
      (event: MessageEvent): event is IncomingPlatformMessage =>
        "tag" in event.data,
    );

    port.subscribe((messageEvent) => {
      const platformEvent = messageEvent.data;

      console.log("GOT MESSAGE!", messageEvent);

      const callbackProps = <
        T extends Record<string, unknown> = Record<string, unknown>,
      >(
        props: T = {} as T,
      ) => ({
        ...props,
        frame: this.frame,
      });

      const sendPopupsBlockedMessage = () => {
        port.send({
          tag: "AuthFailed",
          reason:
            "Oops! Looks your browser is blocking pop-ups, try allowing them in your browser and giving it another go. If you keep having trouble, please contact support!",
        });
      };

      const setAuthInProgress = (inProgress: boolean): void =>
        this.setState((state) => ({
          ...state,
          authenticationInProgress: inProgress,
        }));

      switch (platformEvent.tag) {
        case PlatformAuthEventType.AuthSucceeded: {
          this.callbacks.onAuthSucceeded(callbackProps());

          /**
           * Stop receiving messages. The best thing to do would be to properly
           * manage the state of this hook and any Components that use it, such that
           * after auth succeeds, any listeners are cleaned up so we don't receive
           * messages while in an "impossible" state (ex. transitioning, modal isn't on screen, etc).
           * That includes making sure we're preserving or destroying them as needed when React
           * re-renders.
           *
           * But it's not always easy to do that, so this is going to assume that once we're auth'd,
           * we'll always want to close out the iframe. If that assumption is wrong in the future,
           * then we'll want to remove this line.
           */
          // platform.close();

          return void this.setState((state) => ({
            ...state,
            isAuthenticatedWithVendrPlatform: true,
          }));
        }
        case PlatformAuthEventType.CodeAuthInitiated:
          this.callbacks.onCodeAuthInitiated(callbackProps());
          return;
        case PlatformAuthEventType.CodeAuthInProgress:
          setAuthInProgress(true);
          this.callbacks.onCodeAuthInProgress(callbackProps());
          return;
        case PlatformAuthEventType.CodeAuthFailed:
          setAuthInProgress(false);
          this.callbacks.onCodeAuthFailed(callbackProps());
          return;
        case PlatformAuthEventType.GoogleAuthInitiated:
          this.callbacks.onGoogleAuthInitiated(callbackProps());
          return;
        case PlatformAuthEventType.GoogleAuthInProgress:
          console.log("Google Auth In Progress!");
          setAuthInProgress(true);
          this.callbacks.onGoogleAuthInProgress(callbackProps());
          return;
        case PlatformAuthEventType.GoogleAuthFailed:
          console.log("GOOGLE AUTH FAILED!", platformEvent.reason);
          setAuthInProgress(false);
          if (platformEvent.reason === PlatformAuthFailedReason.PopupsBlocked) {
            sendPopupsBlockedMessage();
          }
          this.callbacks.onGoogleAuthFailed(callbackProps());
          return;
        case PlatformAuthEventType.SamlAuthInitiated:
          this.callbacks.onSamlAuthInitiated(callbackProps());
          return;
        case PlatformAuthEventType.SamlAuthInProgress:
          setAuthInProgress(true);
          this.callbacks.onSamlAuthInProgress(callbackProps());
          return;
        case PlatformAuthEventType.SamlAuthFailed:
          setAuthInProgress(false);
          console.log("GOOGLE AUTH FAILED!", platformEvent.reason);
          if (platformEvent.reason === PlatformAuthFailedReason.PopupsBlocked) {
            sendPopupsBlockedMessage();
          }
          this.callbacks.onSamlAuthFailed(callbackProps());
          return;
        case PlatformAuthEventType.RedirectIframe:
          {
            console.log(
              "Got redirect message! GO TO!",
              platformEvent.redirectTo,
            );
            this.callbacks.onRedirectIframe(
              callbackProps({ redirectTo: platformEvent.redirectTo }),
            );
          }
          return;
        case PlatformAuthEventType.OAuthCancelled:
          setAuthInProgress(false);
          this.callbacks.onOAuthCancelled(callbackProps());
          port.send({
            tag: "AuthFailed",
            reason:
              "Oops! Looks like the sign-in process didn't finish. Want to give it another go? If you keep having trouble, please contact support!",
          });
          return;
        default:
          return never(platformEvent);
      }
    });

    port.connect(connectMessage.data);
  }
}

/**
 * Manages an individual connection to either the window object, or
 * the port that the Platform app has passed to Marketplace
 */
class PlatformChannel<
  EmitterType extends EventTarget = EventTarget,
  IncomingMessageType = PlatformAuthEvent,
  OutgoingMessageType = MarketplaceAuthEvent,
> {
  private readonly platformReceiver: EmitterType;
  private readonly isPlatformMessage: (
    event: MessageEvent,
  ) => event is MessageEvent<IncomingMessageType>;
  private subscriber: null | ((msg: Event) => void);
  private connectSent: boolean;

  constructor(
    platformReceiver: EmitterType,
    isPlatformMessage: (
      event: MessageEvent,
    ) => event is MessageEvent<IncomingMessageType>,
  ) {
    this.platformReceiver = platformReceiver;
    this.isPlatformMessage = isPlatformMessage;
    this.subscriber = null;
    this.connectSent = false;
  }

  connect(event: PlatformConnectEvent) {
    if (!this.connectSent) {
      this.send(event as OutgoingMessageType);
      this.connectSent = true;
    }
  }

  private listen() {
    this.platformReceiver.addEventListener("message", this.subscriber);

    if (this.platformReceiver instanceof MessagePort) {
      this.platformReceiver.start();
    }
  }

  subscribe(listener: (msg: MessageEvent<IncomingMessageType>) => void) {
    if (this.subscriber) {
      return;
    }

    const isPlatformMessage = this.isPlatformMessage;
    this.subscriber = function subscriber(messageEvent: Event) {
      if (
        !(messageEvent instanceof MessageEvent) ||
        !isPlatformMessage(messageEvent)
      ) {
        return;
      }

      const message: MessageEvent<IncomingMessageType> = messageEvent;

      listener(message);
    };

    this.listen();
  }

  unsubscribe() {
    this.platformReceiver.removeEventListener("message", this.subscriber);
    this.subscriber = null;
    if (this.platformReceiver instanceof MessagePort) {
      this.platformReceiver.close();
    }
  }

  send(event: OutgoingMessageType) {
    if (this.platformReceiver instanceof MessagePort) {
      return void this.platformReceiver.postMessage(event);
    }
  }
}
